<div class="page">
  <!-- ===================== HERO HEADER ===================== -->
  <div class="hero">
    <div class="hero-text">
      <div class="hero-icon"><i class="pi pi-building"></i></div>
      <div>
        <h2>{{ pageTitle() || 'Clients' }}</h2>
        <p>Organisations that consume our APIs — open a client to manage branches, keys, payment &amp; messaging.</p>
      </div>
    </div>
    <div class="hero-right">
      <div class="stat">
        <span class="stat-num">{{ totalCount() }}</span>
        <span class="stat-label">Total</span>
      </div>
      <p-divider layout="vertical" />
      <div class="stat">
        <span class="stat-num ok">{{ activeCount() }}</span>
        <span class="stat-label">Active</span>
      </div>
      <p-button label="Add Client" icon="pi pi-plus" styleClass="hero-btn" (onClick)="openAdd()" />
    </div>
  </div>

  <!-- ===================== TABLE CARD ===================== -->
  <div class="card">
    <div class="toolbar">
      <p-iconfield iconPosition="left" class="search-field">
        <p-inputicon styleClass="pi pi-search" />
        <input type="text" pInputText placeholder="Search code, name, company, email or mobile..."
          [ngModel]="search()" (ngModelChange)="onSearch($event)" />
      </p-iconfield>
      <p-chip [label]="rows().length + ' clients'" icon="pi pi-users" styleClass="count-chip" />
    </div>

    <p-table [value]="rows()" [loading]="loading()" dataKey="id" [rows]="10"
      [paginator]="rows().length > 10" [rowsPerPageOptions]="[10, 25, 50]" styleClass="p-datatable-sm clients-table"
      [rowHover]="true" [showCurrentPageReport]="true" currentPageReportTemplate="Showing {first}–{last} of {totalRecords}">
      <ng-template pTemplate="header">
        <tr>
          <th>Client</th>
          <th>Company</th>
          <th>Contact</th>
          <th>Location</th>
          <th class="ta-c">Status</th>
          <th class="ta-r">Actions</th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-r>
        <tr class="clickable" (click)="open(r)">
          <!-- identity: avatar + name + code -->
          <td>
            <div class="client-cell">
              @if (logoSrc(r)) {
                <p-avatar [image]="logoSrc(r)" shape="circle" size="large" />
              } @else {
                <p-avatar [label]="initials(r.clientName)" shape="circle" size="large"
                  styleClass="avatar-brand" />
              }
              <div class="client-meta">
                <span class="c-name">{{ r.clientName }}</span>
                <span class="c-code mono">{{ r.clientCode || '—' }}</span>
              </div>
            </div>
          </td>
          <td>{{ r.companyName || '—' }}</td>
          <!-- contact: person / email / mobile -->
          <td>
            <div class="contact-cell">
              @if (r.contactPerson) { <span class="ct-person"><i class="pi pi-user"></i> {{ r.contactPerson }}</span> }
              @if (r.email) { <span class="ct-line"><i class="pi pi-envelope"></i> {{ r.email }}</span> }
              @if (r.mobile) { <span class="ct-line mono"><i class="pi pi-phone"></i> {{ r.mobile }}</span> }
              @if (!r.contactPerson && !r.email && !r.mobile) { <span class="muted">—</span> }
            </div>
          </td>
          <td>
            @if (r.city || r.state || r.country) {
              <span class="loc"><i class="pi pi-map-marker"></i>
                {{ r.city || '' }}{{ r.state ? (r.city ? ', ' : '') + r.state : '' }}{{ r.country ? ' · ' + r.country : '' }}
              </span>
            } @else { <span class="muted">—</span> }
          </td>
          <td class="ta-c">
            <span class="active-toggle" (click)="$event.stopPropagation(); toggleActive(r)"
              pTooltip="Click to toggle">
              <p-tag [value]="r.isActive ? 'Active' : 'Inactive'"
                [severity]="r.isActive ? 'success' : 'secondary'"
                [icon]="r.isActive ? 'pi pi-check-circle' : 'pi pi-ban'" rounded />
            </span>
          </td>
          <td class="ta-r actions" (click)="$event.stopPropagation()">
            <p-button icon="pi pi-arrow-up-right" [rounded]="true" [text]="true" severity="info"
              pTooltip="Open" [routerLink]="['/clients', r.id]" />
            <p-button icon="pi pi-pencil" [rounded]="true" [text]="true" severity="secondary"
              pTooltip="Edit" (onClick)="openEdit(r)" />
            <p-button icon="pi pi-trash" [rounded]="true" [text]="true" severity="danger"
              pTooltip="Delete" (onClick)="remove(r)" />
          </td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage">
        <tr>
          <td colspan="6">
            <div class="state">
              <i class="pi pi-building"></i>
              <p>No clients found.</p>
              <p-button label="Add your first client" icon="pi pi-plus" [outlined]="true"
                size="small" (onClick)="openAdd()" />
            </div>
          </td>
        </tr>
      </ng-template>
    </p-table>
  </div>
</div>

<!-- ===================== ADD / EDIT MODAL ===================== -->
<p-dialog [visible]="showModal()" (visibleChange)="showModal.set($event)" [modal]="true" [draggable]="true"
  [resizable]="false" [closable]="true" [dismissableMask]="false"
  [header]="editing() ? 'Edit Client' : 'Add Client'" styleClass="menu-dialog"
  [style]="{ width: '720px' }" [breakpoints]="{ '720px': '94vw' }">
  <form [formGroup]="form" class="menu-form">
    <div class="grid-2">
      <div class="field">
        <label>Client Code <span class="req">*</span></label>
        <input pInputText type="text" formControlName="clientCode" placeholder="CLT-0012"
          (input)="upper('clientCode')" [readonly]="editing()" />
        @if (form.get('clientCode')?.touched && form.get('clientCode')?.invalid) {
          <small class="err">Client code is required</small>
        }
      </div>
      <div class="field">
        <label>Client Name <span class="req">*</span></label>
        <input pInputText type="text" formControlName="clientName" placeholder="Acme Health" />
        @if (form.get('clientName')?.touched && form.get('clientName')?.invalid) {
          <small class="err">Client name is required</small>
        }
      </div>
      <div class="field">
        <label>Company Name</label>
        <input pInputText type="text" formControlName="companyName" placeholder="Acme Health Pvt Ltd" />
      </div>
      <div class="field">
        <label>Contact Person</label>
        <input pInputText type="text" formControlName="contactPerson" placeholder="Riya Mehta" />
      </div>
      <div class="field">
        <label>Email</label>
        <input pInputText type="email" formControlName="email" placeholder="riya@acme.com" />
        @if (form.get('email')?.touched && form.get('email')?.invalid) {
          <small class="err">Enter a valid email</small>
        }
      </div>
      <div class="field">
        <label>Mobile</label>
        <input pInputText type="text" formControlName="mobile" placeholder="9876543210" />
        @if (form.get('mobile')?.touched && form.get('mobile')?.invalid) {
          <small class="err">Enter a valid 10-digit mobile</small>
        }
      </div>
    </div>

    <div class="field">
      <label>Address</label>
      <textarea class="form-textarea" rows="2" formControlName="address" placeholder="12 MG Road"></textarea>
    </div>

    <div class="grid-3">
      <div class="field">
        <label>City</label>
        <input pInputText type="text" formControlName="city" placeholder="Pune" />
      </div>
      <div class="field">
        <label>State</label>
        <input pInputText type="text" formControlName="state" placeholder="MH" />
      </div>
      <div class="field">
        <label>Country</label>
        <input pInputText type="text" formControlName="country" placeholder="India" />
      </div>
      <div class="field">
        <label>Pincode</label>
        <input pInputText type="text" formControlName="pincode" placeholder="411001" />
        @if (form.get('pincode')?.touched && form.get('pincode')?.invalid) {
          <small class="err">Enter a valid 6-digit pincode</small>
        }
      </div>
      <div class="field">
        <label>GST No</label>
        <input pInputText type="text" formControlName="gstNo" placeholder="27ABCDE1234F1Z5"
          (input)="upper('gstNo')" />
        @if (form.get('gstNo')?.touched && form.get('gstNo')?.invalid) {
          <small class="err">GST must be 15 characters</small>
        }
      </div>
      <div class="field">
        <label>PAN No</label>
        <input pInputText type="text" formControlName="panNo" placeholder="ABCDE1234F"
          (input)="upper('panNo')" />
        @if (form.get('panNo')?.touched && form.get('panNo')?.invalid) {
          <small class="err">PAN must be 10 characters (ABCDE1234F)</small>
        }
      </div>
    </div>

    <div class="grid-2">
      <div class="field">
        <label>Logo</label>
        <app-doc-upload formControlName="logoUrl" module="ClientLogo" />
        <small class="hint-muted">
          Upload an image (JPG/PNG). It's uploaded immediately and the returned file id is saved with the client.
        </small>
      </div>
      <div class="field">
        <label>Website</label>
        <input pInputText type="text" formControlName="website" placeholder="https://acme.com" />
        @if (form.get('website')?.touched && form.get('website')?.invalid) {
          <small class="err">Enter a valid URL (http/https)</small>
        }
      </div>
    </div>

    <div class="field">
      <label>Remarks</label>
      <textarea class="form-textarea" rows="2" formControlName="remarks" placeholder="Internal notes…"></textarea>
    </div>

    <label class="switch-row">
      <p-toggleSwitch formControlName="isActive" />
      <span>Active</span>
    </label>

    <div class="modal-actions">
      <p-button label="Cancel" [outlined]="true" (onClick)="closeModal()" />
      <p-button [label]="saving() ? 'Saving…' : 'Save'" icon="pi pi-check" (onClick)="save()"
        [disabled]="saving()" [loading]="saving()" />
    </div>
  </form>
</p-dialog>
